/**
 * AuthDialogs - Componentes de Login e Registro
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Dependências:
 * - useAuth (contexto de autenticação)
 * - useTranslation (traduções)
 * - axios
 * - Dialog, Button, Input, Label, Select (shadcn/ui)
 */

import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { useTranslation } from "../translations";
import { useAuth } from "../App";
import { ForgotPasswordDialog } from "./ForgotPasswordDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * LoginDialog - Modal de login
 */
export const LoginDialog = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const { login } = useAuth();
  const { t } = useTranslation();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/login`, { email, password });
      login(response.data.user, response.data.token);
      setOpen(false);
    } catch (error) {
      alert(t('errorLogin'));
    }
    setLoading(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">{t('login')}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('doLogin')}</DialogTitle>
          <DialogDescription>{t('enterCredentials')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <Label htmlFor="email">{t('email')}</Label>
            <Input 
              id="email" 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
            />
          </div>
          <div>
            <Label htmlFor="password">{t('password')}</Label>
            <Input 
              id="password" 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? t('loggingIn') : t('login')}
          </Button>
          <button
            type="button"
            data-testid="forgot-password-link"
            onClick={() => { setOpen(false); setForgotOpen(true); }}
            className="w-full text-center text-sm text-green-700 hover:underline"
          >
            {t('forgotPassword')}
          </button>
        </form>
      </DialogContent>
    </Dialog>
    <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
    </>
  );
};

/**
 * RegisterDialog - Modal de registro
 * 
 * @param {string} variant - Variante do botão: 'hero', 'hero-white', ou padrão
 */
const getCursoSlug = () => {
  try {
    const url = new URLSearchParams(window.location.search).get('curso');
    if (url) return url;
    const c = JSON.parse(localStorage.getItem('curso_src') || 'null');
    if (c?.slug && Date.now() - (c.ts || 0) < 7 * 24 * 3600 * 1000) return c.slug;
  } catch { /* ignora curso_src corrompido */ }
  return "";
};

export const RegisterDialog = ({ variant }) => {
  const [formData, setFormData] = useState(() => ({
    email: "", password: "", name: "", user_type: "", phone: "",
    company: "", experience_years: "", specialization: "", qualificacao_tecnica: "",
    referral_code: (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('ref')) || "",
    curso_slug: typeof window !== 'undefined' ? getCursoSlug() : ""
  }));
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { login } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (variant === "hero" && new URLSearchParams(window.location.search).get("cadastro") === "vaga") {
      setOpen(true);
    }
  }, [variant]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!formData.user_type) {
      alert(t('selectType'));
      return;
    }
    setLoading(true);
    try {
      // Clean up form data before sending
      const cleanData = { ...formData };
      // Convert empty strings to null for optional fields
      if (!cleanData.experience_years && cleanData.experience_years !== 0) cleanData.experience_years = null;
      if (!cleanData.specialization) cleanData.specialization = null;
      if (!cleanData.qualificacao_tecnica) cleanData.qualificacao_tecnica = null;
      if (!cleanData.company) cleanData.company = null;
      if (!cleanData.phone) cleanData.phone = null;
      if (!cleanData.referral_code) cleanData.referral_code = null;
      if (!cleanData.curso_slug) cleanData.curso_slug = null;
      // Convert experience_years to number if present
      if (cleanData.experience_years !== null) cleanData.experience_years = parseInt(cleanData.experience_years);
      
      const response = await axios.post(`${API}/register`, cleanData);
      localStorage.removeItem('curso_src');
      login(response.data.user, response.data.token);
      setOpen(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : t('errorUnknown'));
      alert(t('errorRegister') + ": " + msg);
    }
    setLoading(false);
  };

  // Determinar estilo do botão baseado na variant
  const getButtonStyle = () => {
    if (variant === "hero") {
      return "bg-green-600 hover:bg-green-700 text-white px-8 py-3 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all";
    }
    if (variant === "hero-white") {
      return "bg-white hover:bg-gray-100 text-green-700 px-8 py-3 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all";
    }
    return ""; // Estilo padrão
  };

  const getButtonText = () => {
    if (variant === "hero" || variant === "hero-white") {
      return `🚀 ${t('startFree')}`;
    }
    return t('register');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {variant ? (
          <button className={getButtonStyle()}>
            {getButtonText()}
          </button>
        ) : (
          <Button>{t('register')}</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('createAccount')}</DialogTitle>
          <DialogDescription>{t('registerOnPlatform')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleRegister} className="space-y-4">
          <div>
            <Label htmlFor="name">{t('fullName')}</Label>
            <Input 
              id="name" 
              value={formData.name} 
              onChange={(e) => setFormData({...formData, name: e.target.value})} 
              required 
            />
          </div>
          <div>
            <Label htmlFor="email">{t('email')}</Label>
            <Input 
              id="email" 
              type="email" 
              value={formData.email} 
              onChange={(e) => setFormData({...formData, email: e.target.value})} 
              required 
            />
          </div>
          <div>
            <Label htmlFor="password">{t('password')}</Label>
            <Input 
              id="password" 
              type="password" 
              value={formData.password} 
              onChange={(e) => setFormData({...formData, password: e.target.value})} 
              required 
            />
          </div>
          <div>
            <Label htmlFor="user_type">{t('userType')} *</Label>
            <Select value={formData.user_type} onValueChange={(value) => setFormData({...formData, user_type: value})} required>
              <SelectTrigger>
                <SelectValue placeholder={t('selectType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="employer">{t('employer')}</SelectItem>
                <SelectItem value="professional">{t('professional')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="phone">{t('phone')}</Label>
            <Input 
              id="phone" 
              value={formData.phone} 
              onChange={(e) => setFormData({...formData, phone: e.target.value})} 
            />
          </div>
          {formData.user_type === "employer" && (
            <>
              <div>
                <Label htmlFor="company">{t('company')}</Label>
                <Input 
                  id="company" 
                  value={formData.company} 
                  onChange={(e) => setFormData({...formData, company: e.target.value})} 
                />
              </div>
              <div>
                <Label htmlFor="qualificacao_tecnica">{t('technicalQualification')}</Label>
                <Select value={formData.qualificacao_tecnica || ""} onValueChange={(value) => setFormData({...formData, qualificacao_tecnica: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('selectQualification')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="engenheiro_agronomo">{t('qualificationEngineer')}</SelectItem>
                    <SelectItem value="tecnico_agricola">{t('qualificationTechnician')}</SelectItem>
                    <SelectItem value="lider_equipe">{t('qualificationLeader')}</SelectItem>
                    <SelectItem value="proprietario">{t('qualificationOwner')}</SelectItem>
                    <SelectItem value="outro">{t('qualificationOther')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">{t('technicalQualificationDesc')}</p>
              </div>
            </>
          )}
          {formData.user_type === "professional" && (
            <>
              <div>
                <Label htmlFor="experience_years">{t('experienceYears')}</Label>
                <Input 
                  id="experience_years" 
                  type="number" 
                  value={formData.experience_years} 
                  onChange={(e) => setFormData({...formData, experience_years: e.target.value})} 
                />
              </div>
              <div>
                <Label htmlFor="specialization">{t('specialization')} *</Label>
                <Select value={formData.specialization || ""} onValueChange={(value) => setFormData({...formData, specialization: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('selectSpecialization')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sangrador">{t('tapper')}</SelectItem>
                    <SelectItem value="gerente">{t('generalManager')}</SelectItem>
                    <SelectItem value="fiscal">{t('inspector')}</SelectItem>
                    <SelectItem value="agronomo">{t('agronomist')}</SelectItem>
                    <SelectItem value="outros">{t('other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div>
            <Label htmlFor="referral_code">Código de indicação (opcional)</Label>
            <Input
              id="referral_code"
              data-testid="register-referral-code"
              placeholder="Ex: HEVEAVINI1234"
              value={formData.referral_code}
              onChange={(e) => setFormData({...formData, referral_code: e.target.value.toUpperCase()})}
            />
            {formData.referral_code && (
              <p className="text-xs text-green-600 mt-1">✅ Código aplicado — quem te indicou ganha o bônus quando você assinar um plano</p>
            )}
          </div>
          {formData.curso_slug && (
            <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2" data-testid="register-curso-note">
              🎓 Cadastro vindo do curso de sangria — seu perfil ganhará o selo de aluno do curso!
            </p>
          )}
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? t('registering') : t('register')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
